import React, { useMemo, useState } from 'react';
import { ArrowLeft, Link2, Link2Off, Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import { EinweisungOnlineTeilnahme, SigeEinweisung } from '../sige-plan/types';
import {
  einweisungTitel,
  formatDatum,
  linkIstAktiv,
  onlineTeilnehmerName,
  sortiereOnlineTeilnahmen,
  zaehleTeilnahmen,
} from './einweisungLogic';
import {
  beschreibeTeilnahmenKopf,
  filtereOnlineNachFirma,
  firmenAusTeilnahmen,
  formatZeitpunkt,
} from './onlineTeilnahmenLogic';

interface Props {
  einweisung: SigeEinweisung;
  /** Die Online-Teilnahmen DIESER Einweisung (Container filtert vor). */
  online: EinweisungOnlineTeilnahme[];
  isMobile?: boolean;
  /** Laeuft gerade das Nachladen? */
  aktualisiert?: boolean;
  onAktualisieren: () => void;
  onZurueck: () => void;
}

/**
 * Die Online-Teilnahmen einer Einweisung — wer hat ueber den Link/QR gelesen
 * und unterschrieben.
 *
 * NUR ANSICHT. Kein Loeschen, kein Bearbeiten (Betreiber 12.09.): eine
 * Online-Teilnahme ist der Nachweis, dass die Person eingewiesen wurde; die
 * Tabelle laesst `authenticated` ohnehin nur lesen. Wer einen Eintrag fuer
 * falsch haelt, vermerkt das im Blatt — er verschwindet nicht.
 *
 * Kein Realtime: der Knopf „Aktualisieren" laedt nach, und beim Oeffnen der
 * Ablage wird ohnehin frisch gelesen.
 */
export const OnlineTeilnahmen: React.FC<Props> = ({
  einweisung,
  online,
  isMobile = false,
  aktualisiert = false,
  onAktualisieren,
  onZurueck,
}) => {
  const [firma, setFirma] = useState('');
  const stand = zaehleTeilnahmen(einweisung, online);
  const firmen = useMemo(() => firmenAusTeilnahmen(online), [online]);
  const sichtbar = useMemo(
    () => sortiereOnlineTeilnahmen(filtereOnlineNachFirma(online, firma)),
    [online, firma],
  );
  const linkAktiv = linkIstAktiv(einweisung);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-slate-800 truncate">{einweisungTitel(einweisung)}</h2>
          <p className="text-xs text-slate-500 truncate">
            Nr. {einweisung.nummer} · {formatDatum(einweisung.datum)}
          </p>
          <p className="text-sm font-semibold text-slate-700 mt-1">{beschreibeTeilnahmenKopf(stand)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {linkAktiv ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded-full">
              <Link2 className="w-3 h-3" /> Link aktiv
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold text-slate-500 bg-slate-50 border border-slate-200 rounded-full">
              <Link2Off className="w-3 h-3" /> Link aus
            </span>
          )}
          <button
            onClick={onAktualisieren}
            disabled={aktualisiert}
            className="min-h-[44px] px-3 flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-40"
          >
            {aktualisiert ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Aktualisieren
          </button>
          <button
            onClick={onZurueck}
            className="min-h-[44px] px-3 flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Zurück
          </button>
        </div>
      </div>

      {firmen.length > 0 && (
        <label className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
          <span className="font-semibold">Firma</span>
          <select
            value={firma}
            onChange={(e) => setFirma(e.target.value)}
            className="min-h-[40px] px-3 text-sm text-slate-800 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="">Alle Firmen ({online.length})</option>
            {firmen.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          {firma && (
            <span className="text-slate-400">{sichtbar.length} von {online.length}</span>
          )}
        </label>
      )}

      {online.length === 0 ? (
        <div className="p-8 bg-white border border-dashed border-slate-300 rounded-2xl text-center">
          <p className="text-sm text-slate-600 font-semibold">Noch keine Online-Teilnahme</p>
          <p className="text-xs text-slate-500 mt-1">
            {linkAktiv
              ? 'Sobald jemand den QR-Code scannt, liest und unterschreibt, erscheint er hier.'
              : 'Der Link ist nicht aktiv. In der Ablage „Link" drücken, dann QR-Code oder Aushang drucken.'}
          </p>
        </div>
      ) : sichtbar.length === 0 ? (
        <div className="p-6 bg-white border border-dashed border-slate-300 rounded-2xl text-center text-xs text-slate-500">
          Keine Teilnahme dieser Firma.
        </div>
      ) : isMobile ? (
        <div className="space-y-2">
          {sichtbar.map((t) => (
            <div key={t.id} className="bg-white border border-slate-200 rounded-2xl p-3 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-slate-800 truncate">
                  {onlineTeilnehmerName(t)}
                  {(t.funktion || '').trim() && <span className="font-normal text-slate-500">, {t.funktion!.trim()}</span>}
                </p>
                <p className="text-xs text-slate-500 truncate">{t.firma}</p>
                <p className="text-xs text-slate-400">{formatZeitpunkt(t.unterschrieben_am)}</p>
              </div>
              <img
                src={t.unterschrift}
                alt={`Unterschrift ${onlineTeilnehmerName(t)}`}
                className="max-h-10 w-24 object-contain bg-white border border-slate-200 rounded shrink-0"
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table data-tutorial="einweisung.teilnahmen-tabelle" className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="px-4 py-2 text-left font-bold">Name</th>
                <th className="px-4 py-2 text-left font-bold">Funktion</th>
                <th className="px-4 py-2 text-left font-bold">Firma</th>
                <th className="px-4 py-2 text-left font-bold">Zeitpunkt</th>
                <th className="px-4 py-2 text-left font-bold">Unterschrift</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sichtbar.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50/60">
                  <td className="px-4 py-2 font-semibold text-slate-800">{onlineTeilnehmerName(t)}</td>
                  <td className="px-4 py-2 text-slate-600">{(t.funktion || '').trim() || <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-2 text-slate-600">{t.firma}</td>
                  <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{formatZeitpunkt(t.unterschrieben_am)}</td>
                  <td className="px-4 py-2">
                    <img
                      src={t.unterschrift}
                      alt={`Unterschrift ${onlineTeilnehmerName(t)}`}
                      className="max-h-10 w-28 object-contain bg-white border border-slate-200 rounded"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-slate-400 flex items-start gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-px" />
        <span>
          Online-Teilnahmen sind Nachweis und werden nicht gelöscht. Sie erscheinen im PDF der Einweisung
          hinter den auf dem Gerät erfassten Teilnehmern, gekennzeichnet mit „online" und Zeitstempel.
        </span>
      </p>
    </div>
  );
};
